import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
} from "@mui/material";
import {
  ContentCopy,
  Email,
  Phone,
  Language,
  LocationOn,
  Person,
  Extension,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { backend_api } from "../../../static/server";
import { getContactDetail } from "../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { getAllCustomField } from "../../../redux/Apps/Lawyerapp/CustomFieldSlice";
import { useParams, useNavigate } from "react-router-dom";
import UserAvatar from "../../../components/UserAvatar";
import {
  calculateContrastColor,
  formatteDateDay,
  formattedDate,
  pageScrollStyle,
  generateUniqueMatterName,
} from "../../../styles/styles";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { AccountCircleRounded } from "@mui/icons-material";
import {
  getCases,
  getPracticeArea,
} from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import DataTable from "../../../components/DataTable";
import EmptyBox from "../../../assets/empty-box.webp";
import noBillsImg from "../../../assets/no-bills.webp";
import { getAllBilles } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { generateInvoiceNumber } from "../../../components/LawyerCompo/Billing/Billes";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  getAllStatement,
  getAllAccounts,
} from "../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import { getAllDocFolder } from "../../../redux/Apps/Lawyerapp/DocumentSlice/DocumentSlice";
import TopicRoundedIcon from "@mui/icons-material/TopicRounded";

const CustomAccordion = ({ expanded, onChange, title, children }) => (
  <Accordion
    disableGutters
    elevation={0}
    square
    expanded={expanded}
    onChange={onChange}
    sx={{
      borderRadius: "0.6rem",
      border: "1px solid #e5e5e5",
      backgroundColor: "#fff",
      boxShadow: "none",
      "&::before": { display: "none" },
    }}
  >
    <AccordionSummary
      expandIcon={<KeyboardArrowDownRounded />}
      sx={{
        padding: "0.5rem 1rem",
        borderBottom: (theme) => (expanded ? "1px solid #e5e5e5" : "none"),
        minHeight: "unset",
        "& .MuiAccordionSummary-content": {
          margin: "0rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        },
      }}
    >
      {typeof title === "string" ? (
        <Typography
          variant="body1"
          sx={{ fontSize: "1.1rem", fontWeight: 600 }}
        >
          {title}
        </Typography>
      ) : (
        title
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ padding: "0rem", pt: "0.5rem" }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

const ContactDashboard = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { contactDetail } = useSelector((state) => state.contact);
  const { allCustomField } = useSelector((state) => state.customField);
  const { token } = useSelector((data) => data.user);
  const { allCase, allPracticeArea, loading } = useSelector(
    (state) => state.case
  );
  const { pageSize, rowCount, nextPage, previousPage, count } = useSelector(
    (state) => state.case.casePagination
  );

  const { allBilles, loading: billsLoading } = useSelector(
    (state) => state.billing
  );

  const {
    pageSize: billPageSize,
    rowCount: billRowCount,
    nextPage: billNextPage,
    previousPage: billPreviousPage,
    count: billCount,
  } = useSelector((state) => state.billing.pagination);

  const { allStatement, loading: transactionLoading } = useSelector(
    (state) => state.account
  );

  const { AllAccounts } = useSelector((state) => state.account);
  const { allDocFolder, loading: documentLoading } = useSelector(
    (state) => state.document
  );

  const {
    pageSize: docPageSize,
    rowCount: docRowCount,
    nextPage: docNextPage,
    previousPage: docPreviousPage,
    count: docCount,
  } = useSelector((state) => state.document.docFoldersPagination);

  const {
    pageSize: transactionPageSize,
    rowCount: transactionRowCount,
    nextPage: transactionNextPage,
    previousPage: transactionPreviousPage,
    count: transactionCount,
  } = useSelector((state) => state.account.statementPagination);

  const [copiedField, setCopiedField] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [personalInfoExpand, setPersonalInfoExpand] = useState(true);
  const [addressInfoExpand, setAddressInfoExpand] = useState(true);
  const [customFieldExpand, setCustomFieldExpand] = useState(true);
  const [contactInfoExpand, setContactInfoExpand] = useState(true);
  const [page, setPage] = useState(1);
  const [billPage, setBillPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const [documentPage, setDocumentPage] = useState(1);
  const [documentSearchTerm, setDocumentSearchTerm] = useState("");
  const [selectedBank, setSelectedBank] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      getContactDetail({
        url: `${backend_api}contact/retrieve/${id}/`,
        token: token,
      })
    );
    dispatch(
      getAllCustomField({
        url: `${backend_api}custom-field/?field_for=contact`,
        token: token,
      })
    );
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );
    dispatch(
      getCases({
        url: `${backend_api}case/register/?case_related_person=${id}`,
        token: token,
      })
    );
    dispatch(
      getAllBilles({
        url: `${backend_api}firm-case/bill/?client=${id}`,
        token: token,
      })
    );
    dispatch(
      getAllStatement({
        url: `${backend_api}bank/transactions/?client=${id}${
          selectedBank ? `&account=${selectedBank.id}` : ""
        }`,
        token: token,
      })
    );
    dispatch(
      getAllAccounts({ url: `${backend_api}bank/accounts/`, token: token })
    );
    dispatch(
      getAllDocFolder({
        url: `${backend_api}documents/document/folder/?parent__isnull=true&client=${id}`,
        token: token,
        // page: page,
        // pageSize: pageSize,
        // teamMemberLawyerId: teamMemberLawyerId,
      })
    );
  }, [id, token, dispatch, selectedBank]);

  const customFieldDetails = useMemo(() => {
    if (!contactDetail?.custom_fields_data) return [];
    return contactDetail.custom_fields_data.map((field) => {
      const info = allCustomField?.find((f) => f.id === field.id);
      return {
        name: info?.field_name || `Field ${field.id}`,
        value: field.value,
      };
    });
  }, [contactDetail, allCustomField]);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setSnackbarOpen(true);
      setTimeout(() => setCopiedField(""), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const CopyIconButton = ({ text, fieldName }) => (
    <Tooltip
      title={copiedField === fieldName ? "Copied!" : "Copy to clipboard"}
    >
      <IconButton
        size="small"
        onClick={() => copyToClipboard(text, fieldName)}
        sx={{
          ml: 1,
          color: copiedField === fieldName ? "success.main" : "grey.400",
          "&:hover": { color: "primary.main" },
        }}
      >
        <ContentCopy fontSize="small" sx={{ fontSize: "0.9rem" }} />
      </IconButton>
    </Tooltip>
  );

  const InfoRow = ({
    label,
    value,
    fieldName,
    icon: Icon,
    color = "grey.600",
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1,
        borderBottom: "1px solid",
        borderColor: "grey.100",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
        {Icon && <Icon sx={{ color, mr: 1.5, fontSize: 18 }} />}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "grey.700",
            minWidth: 80,
            flexShrink: 0,
          }}
        >
          {label}:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "grey.900",
            ml: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
          title={value || "Not specified"}
        >
          {value || "Not specified"}
        </Typography>
      </Box>
      {value && <CopyIconButton text={value} fieldName={fieldName} />}
    </Box>
  );

  const fullAddress = useMemo(() => {
    if (!contactDetail) return "";
    return `${contactDetail?.address_1 || ""} ${
      contactDetail?.address_2 || ""
    }, ${contactDetail?.city || ""}, ${contactDetail?.state || ""}, ${
      contactDetail?.country || ""
    } ${contactDetail?.pincode || ""}`.trim();
  }, [contactDetail]);

  const CustomNoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        p: "2rem",
        gap: "1rem",
      }}
    >
      <Box sx={{ height: "10rem" }}>
        <img
          src={EmptyBox}
          alt="Company Logo"
          style={{ height: "100%", width: "100%", objectFit: "cover" }}
          onError={(e) => {
            e.onerror = null;
            e.target.src = require("../../../assets/no-preview.webp");
          }}
        />
      </Box>
      <Typography variant="body1">No case available.</Typography>
    </Box>
  );

  const CustomBillNoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        p: "2rem",
        gap: "1rem",
      }}
    >
      <Box sx={{ height: "9rem" }}>
        <img
          src={noBillsImg}
          alt="No Bills"
          style={{ height: "100%", width: "100%", objectFit: "cover" }}
          onError={(e) => {
            e.onerror = null;
            e.target.src = require("../../../assets/no-preview.webp");
          }}
        />
      </Box>
      <Typography variant="body1">Bills not available.</Typography>
    </Box>
  );

  const columns = [
    { field: "case_name", headerName: "Matter", width: 150 },
    { field: "case_description", headerName: "Description", width: 200 },
    {
      field: "responsible_attorney",
      headerName: "Responsible Attorney",
      width: 160,
    },
    {
      field: "originating_attorney",
      headerName: "Originating Attorney",
      width: 150,
    },
    { field: "practice_area", headerName: "Practice area", width: 200 },
    {
      field: "case_status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body1"
          sx={{
            p: "0.2rem 0.5rem",
            borderRadius: "1rem",
            textAlign: "center",
            fontSize: "0.8rem",
            fontWeight: 500,
            width: "6rem",
            bgcolor:
              params?.row?.case_status === "open"
                ? "#4CAF50"
                : params?.row?.case_status === "pending"
                ? "#FFC107"
                : "#F44336",
            color: "#fff",
          }}
        >
          {params?.value}
        </Typography>
      ),
    },
    { field: "open_date", headerName: "Open Date", width: 150 },
  ];

  const rows = useMemo(() => {
    if (!Array.isArray(allCase)) return [];
    return allCase.map((item) => ({
      id: item?.id,
      case_name: item?.case_name
        ? generateUniqueMatterName(item?.id, item?.case_name)
        : "-",
      case_description: item?.case_description ?? "-",
      responsible_attorney: item?.responsible_attorney ?? "-",
      originating_attorney: item?.originating_attorney ?? "-",
      practice_area: item?.practice_area
        ? allPracticeArea?.find((area) => area?.id === item?.practice_area)
            ?.name
        : "-",
      case_status: item?.case_status ?? "-",
      open_date: item?.open_date ? formatteDateDay(item?.open_date) : "-",
    }));
  }, [allCase, allPracticeArea]);

  const handlePageChange = (newPage) => {
    dispatch(
      getCases({
        url: `${backend_api}case/register/?case_related_person=${id}`,
        token: token,
        page: newPage,
        pageSize: pageSize,
      })
    );
  };

  const billColumns = [
    { field: "invoice_num", headerName: "Invoice Number", width: 150 },
    { field: "last_sent", headerName: "Last sent", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const statusColors = {
          draft: "info",
          pending: "warning",
          unpaid: "error",
          paid: "success",
        };
        return (
          <Chip
            size="small"
            label={
              params.value === "draft"
                ? "Draft"
                : params.value === "pending"
                ? "Pending Approval"
                : params.value === "unpaid"
                ? "Unpaid"
                : params.value === "paid"
                ? "Paid"
                : "-"
            }
            color={statusColors[params.value] || "default"}
            variant="outlined"
          />
        );
      },
    },
    { field: "due", headerName: "Due", width: 160 },
    { field: "case", headerName: "Matter", width: 150 },
    { field: "issue_date", headerName: "Issue Date", width: 150 },
    { field: "due_date", headerName: "Due Date", width: 150 },
    { field: "balance", headerName: "Balance", width: 150 },
  ];

  const billRows = useMemo(() => {
    if (!Array.isArray(allBilles)) return [];
    const filtered = allBilles.filter(
      (item) =>
        item?.client === parseInt(id) ||
        item?.case?.case_related_person === parseInt(id) ||
        item?.case?.case_related_person?.id === parseInt(id)
    );

    return filtered.map((item) => ({
      id: item?.id ?? "-",
      last_sent: item?.last_sent ?? "-",
      invoice_num: item?.id ? generateInvoiceNumber(item?.id) : "-",
      status: item?.status ?? "-",
      due: item?.due_date ? formatteDateDay(item?.due_date) : "-",
      case: item?.case?.id
        ? generateUniqueMatterName(item?.case?.id, item?.case?.case_name)
        : "-",
      issue_date: item?.created_at ? formatteDateDay(item?.created_at) : "-",
      due_date: item?.due_date ? formatteDateDay(item?.due_date) : "-",
      balance: item?.total_amount ?? "-",
    }));
  }, [allBilles, id]);

  const handleBillPageChange = (newPage) => {
    dispatch(
      getAllBilles({
        url: `${backend_api}firm-case/bill/?client=${id}`,
        token: token,
        page: newPage,
        pageSize: billPageSize,
      })
    );
  };

  const transactionColumns = [
    { field: "date", headerName: "Date", width: 150 },
    { field: "bank_name", headerName: "Bank Name", width: 180 },
    { field: "reference", headerName: "Reference", width: 150 },
    { field: "payer_payee", headerName: "Payer/Payee", width: 150 },
    { field: "matter", headerName: "Matter", width: 150 },
    { field: "description", headerName: "Description", width: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (item) => (
        <Chip
          size="small"
          label={item.value}
          color={item.value === "completed" ? "success" : "warning"}
          variant="outlined"
        />
      ),
    },
    { field: "amount", headerName: "Amount ($)", width: 120 },
    {
      field: "running_balance",
      headerName: "Balance ($)",
      width: 150,
      renderCell: (item) => (
        <Typography variant="body2" color="text.secondary">
          {item.value}
        </Typography>
      ),
    },
  ];

  const transactionRows = useMemo(() => {
    if (!Array.isArray(allStatement)) return [];
    return allStatement.map((item) => ({
      id: item?.id,
      date: item?.date ? formatteDateDay(item?.date) : "-",
      bank_name: item?.source_account?.account_name || "-",
      reference: item?.check_reference_no || "-",
      payer_payee: item?.payer_payee || "-",
      matter: item?.matter
        ? generateUniqueMatterName(item?.matter?.id, item?.matter?.case_name)
        : "-",
      description: item?.description || "-",
      status: item?.status || "pending",
      amount: item?.amount ?? "-",
      running_balance: item?.balance_amount || "-",
    }));
  }, [allStatement]);

  const handleTransactionPageChange = (newPage) => {
    const bankQuery = selectedBank ? `&account=${selectedBank.id}` : "";
    dispatch(
      getAllStatement({
        url: `${backend_api}bank/transactions/?client=${id}${bankQuery}`,
        token: token,
        page: newPage,
        pageSize: transactionPageSize,
      })
    );
  };

  const handleDocumentPageChange = (newPage) => {
    dispatch(
      getAllDocFolder({
        url: `${backend_api}documents/document/folder/?parent__isnull=true`,
        token: token,
        page: newPage,
        pageSize: docPageSize,
      })
    );
  };

  const handleOpenFolder = (row) => {
    if (row?.id && row?.folder_name) {
      navigate(`/dashboard/documents/folder/${row.id}-${row.folder_name}/`);
    }
  };

  const handleDocumentSearchChange = (event) => {
    setDocumentSearchTerm(event.target.value);
  };

  const documentColumns = useMemo(
    () => [
      {
        field: "action",
        headerName: "Actions",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open folder">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenFolder(params.row)}
            >
              <TopicRoundedIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        ),
      },
      { field: "folder_name", headerName: "Folder Name", width: 200, flex: 1 },
      { field: "category", headerName: "Category", width: 150 },
      { field: "client", headerName: "Contact", width: 150 },
      { field: "matter", headerName: "Matter", width: 150 },
      {
        field: "file_folderCount",
        headerName: "Folders and files",
        width: 150,
        flex: 1,
      },
      {
        field: "created_at",
        headerName: "Folder Create At",
        width: 150,
        flex: 1,
      },
    ],
    [handleOpenFolder]
  );

  const documentRows = useMemo(() => {
    if (!Array.isArray(allDocFolder)) return [];

    let filteredData = allDocFolder;

    if (documentSearchTerm.trim()) {
      filteredData = allDocFolder.filter((item) => {
        const folderNameMatch = item?.folder_name
          ?.toLowerCase()
          .includes(documentSearchTerm.toLowerCase());
        const categoryMatch = item?.category?.category_name
          ?.toLowerCase()
          .includes(documentSearchTerm.toLowerCase());
        const commentsMatch = item?.comments
          ?.toLowerCase()
          .includes(documentSearchTerm.toLowerCase());

        return folderNameMatch || categoryMatch || commentsMatch;
      });
    }

    return filteredData.map((item) => ({
      id: item?.id,
      folder_name: item?.folder_name ?? "--",
      category: item?.category?.category_name ?? "-",
      client: item?.client
        ? `${item?.case?.case_related_person?.first_name} ${item?.case?.case_related_person?.last_name}`
        : item?.case && item?.case?.case_related_person
        ? `${item?.case?.case_related_person?.first_name} ${item?.case?.case_related_person?.last_name}`
        : "-",
      matter: item?.case
        ? generateUniqueMatterName(item?.case?.id, item?.case?.case_name)
        : "-",
      file_folderCount: (() => {
        const folderCount = Array.isArray(item?.child_folder)
          ? item.child_folder.length
          : 0;
        const fileCount = Array.isArray(item?.documents)
          ? item.documents.length
          : 0;
        return `folders ${folderCount} - files ${fileCount}`;
      })(),
      created_at: item?.created_at ? formatteDateDay(item.created_at) : "-",
    }));
  }, [allDocFolder, documentSearchTerm]);

  return (
    <div className="w-full">
      <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div className="flex justify-between bg-white rounded-lg items-center shadow-md p-2">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.8rem",
                flex: 1,
              }}
            >
              <UserAvatar
                firstName={contactDetail?.first_name}
                imgSrc={contactDetail?.avatar}
                height="2.5rem"
                width="2.5rem"
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.2rem",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.3rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                    }}
                  >
                    {contactDetail?.prefix_name} {contactDetail?.first_name}{" "}
                    {contactDetail?.middle_name} {contactDetail?.last_name}{" "}
                    {contactDetail?.suffix_name}
                  </Typography>
                  <Tooltip
                    title={`Contact type: ${contactDetail?.contact_type}`}
                  >
                    {contactDetail?.contact_type === "company" ? (
                      <BusinessRoundedIcon
                        sx={{ fontSize: "0.9rem", color: "#2e77f5" }}
                      />
                    ) : (
                      <AccountCircleRounded
                        sx={{ fontSize: "0.9rem", color: "#bf3fe1" }}
                      />
                    )}
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.3rem",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Role: {contactDetail?.role || "Contact"}
                  </Typography>

                  {contactDetail?.tags?.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        height: 20,
                        bgcolor: tag?.metadata?.color,
                        color: calculateContrastColor(
                          tag?.metadata?.color || "#fff"
                        ),
                        fontWeight: 500,
                        fontSize: "0.7rem",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            <Box>btns will be there</Box>
          </Box>
        </div>

        <Box
          className="rounded-lg h-[80vh] overflow-y-scroll w-full "
          sx={{ pr: "1rem", ...pageScrollStyle, position: "relative" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "0.5rem",
                p: "0.2rem",
                borderRadius: "0.5rem",
                border: "1px solid #e5e5e5",
                width: "fit-content",
                position: "sticky",
                top: "0rem",
                zIndex: "99",
                backdropFilter: "blur(4px)",
              }}
            >
              <Button
                size="small"
                variant={activeTab === 0 ? "contained" : "text"}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.8rem",
                  transition: "all 0.2s",
                }}
                onClick={() => setActiveTab(0)}
              >
                Personal Information
              </Button>
              <Button
                size="small"
                variant={activeTab === 1 ? "contained" : "text"}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.8rem",
                  transition: "all 0.2s",
                }}
                onClick={() => setActiveTab(1)}
              >
                Matter
              </Button>
              <Button
                size="small"
                variant={activeTab === 2 ? "contained" : "text"}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.8rem",
                  transition: "all 0.2s",
                }}
                onClick={() => setActiveTab(2)}
              >
                Bills
              </Button>
              <Button
                size="small"
                variant={activeTab === 3 ? "contained" : "text"}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.8rem",
                  transition: "all 0.2s",
                }}
                onClick={() => setActiveTab(3)}
              >
                Transactions
              </Button>
              <Button
                size="small"
                variant={activeTab === 4 ? "contained" : "text"}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "0.8rem",
                  transition: "all 0.2s",
                }}
                onClick={() => setActiveTab(4)}
              >
                Documents
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {activeTab === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <CustomAccordion
                      expanded={personalInfoExpand}
                      onChange={() =>
                        setPersonalInfoExpand(!personalInfoExpand)
                      }
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <Person
                            sx={{
                              fontSize: "1.125rem",
                              color: "#6b7280",
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: "1rem",
                              color: "#374151",
                            }}
                          >
                            Personal Information
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          p: "1rem",
                          backgroundColor: "#fafafa",
                          borderRadius: "0.375rem",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Grid container spacing="1rem">
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Prefix Name
                              </Typography>
                              <TextField
                                value={contactDetail?.prefix_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                First Name
                              </Typography>
                              <TextField
                                value={contactDetail?.first_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                  endAdornment: contactDetail?.first_name && (
                                    <CopyIconButton
                                      text={contactDetail.first_name}
                                      fieldName="firstName"
                                    />
                                  ),
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Middle Name
                              </Typography>
                              <TextField
                                value={contactDetail?.middle_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Last Name
                              </Typography>
                              <TextField
                                value={contactDetail?.last_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                  endAdornment: contactDetail?.last_name && (
                                    <CopyIconButton
                                      text={contactDetail.last_name}
                                      fieldName="lastName"
                                    />
                                  ),
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Suffix Name
                              </Typography>
                              <TextField
                                value={contactDetail?.suffix_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Nick Name
                              </Typography>
                              <TextField
                                value={contactDetail?.nick_name || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                  endAdornment: contactDetail?.nick_name && (
                                    <CopyIconButton
                                      text={contactDetail.nick_name}
                                      fieldName="nickName"
                                    />
                                  ),
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Date of Birth
                              </Typography>
                              <TextField
                                value={
                                  formatteDateDay(contactDetail?.d_o_b) || ""
                                }
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                  endAdornment: contactDetail?.d_o_b && (
                                    <CopyIconButton
                                      text={contactDetail.d_o_b}
                                      fieldName="dob"
                                    />
                                  ),
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Company Name
                              </Typography>
                              <TextField
                                value={
                                  contactDetail?.company_self?.company_name ||
                                  ""
                                }
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Title
                              </Typography>
                              <TextField
                                value={contactDetail?.title || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </CustomAccordion>

                    {/* Address Information */}
                    <CustomAccordion
                      expanded={addressInfoExpand}
                      onChange={() => setAddressInfoExpand(!addressInfoExpand)}
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <LocationOn
                            sx={{
                              fontSize: "1.125rem",
                              color: "#6b7280",
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: "1rem",
                              color: "#374151",
                            }}
                          >
                            Address Information
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          p: "1rem",
                          backgroundColor: "#fafafa",
                          borderRadius: "0.375rem",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Grid container spacing="1rem">
                          <Grid item xs={12}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Full Address
                              </Typography>
                              <TextField
                                value={contactDetail?.address_1 || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                  endAdornment: contactDetail?.address_1 && (
                                    <CopyIconButton
                                      text={contactDetail.address_1}
                                      fieldName="address"
                                    />
                                  ),
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                City
                              </Typography>
                              <TextField
                                value={contactDetail?.city || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                State
                              </Typography>
                              <TextField
                                value={contactDetail?.state || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Country
                              </Typography>
                              <TextField
                                value={contactDetail?.country || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.025rem",
                                  mb: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Pin Code
                              </Typography>
                              <TextField
                                value={contactDetail?.pincode || ""}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Not specified"
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    fontSize: "0.875rem",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "0.375rem",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#d1d5db",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#9ca3af",
                                      },
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </CustomAccordion>

                    {contactDetail?.custom_fields &&
                      contactDetail.custom_fields.length > 0 && (
                        <CustomAccordion
                          expanded={customFieldExpand}
                          onChange={() =>
                            setCustomFieldExpand(!customFieldExpand)
                          }
                          title={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <Extension
                                sx={{
                                  fontSize: "1.125rem",
                                  color: "#6b7280",
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                  color: "#374151",
                                }}
                              >
                                Custom Fields
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              p: "1rem",
                              backgroundColor: "#fafafa",
                              borderRadius: "0.375rem",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <Grid container spacing="1rem">
                              {contactDetail?.custom_fields_data?.map(
                                (customField, index) => {
                                  const fieldDefinition =
                                    contactDetail?.custom_fields?.find(
                                      (field) => field.id === customField.id
                                    );

                                  return (
                                    <Grid item xs={12} sm={6} key={index}>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                            color: "#6b7280",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.025rem",
                                            mb: "0.25rem",
                                            display: "block",
                                          }}
                                        >
                                          {fieldDefinition?.field_name ||
                                            `Custom Field ${index + 1}`}
                                        </Typography>
                                        <TextField
                                          value={customField.value || ""}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          placeholder="Not specified"
                                          multiline={
                                            fieldDefinition?.field_type ===
                                            "textarea"
                                          }
                                          rows={
                                            fieldDefinition?.field_type ===
                                            "textarea"
                                              ? 3
                                              : 1
                                          }
                                          InputProps={{
                                            readOnly: true,
                                            sx: {
                                              fontSize: "0.875rem",
                                              backgroundColor: "#ffffff",
                                              borderRadius: "0.375rem",
                                              "& .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "#d1d5db",
                                                },
                                              "&:hover .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "#9ca3af",
                                                },
                                            },
                                            endAdornment: customField.value && (
                                              <CopyIconButton
                                                text={customField.value}
                                                fieldName={`customField_${index}`}
                                              />
                                            ),
                                          }}
                                        />
                                        {fieldDefinition?.field_description && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.6875rem",
                                              color: "#9ca3af",
                                              fontStyle: "italic",
                                              mt: "0.25rem",
                                              display: "block",
                                            }}
                                          >
                                            {fieldDefinition.field_description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Grid>
                                  );
                                }
                              )}
                            </Grid>
                          </Box>
                        </CustomAccordion>
                      )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <DataTable
                      columns={columns}
                      rows={rows}
                      CustomNoRowsOverlay={CustomNoRowsOverlay}
                      loading={loading}
                      page={page}
                      setPage={setPage}
                      pageSize={pageSize}
                      rowCount={count}
                      onPageChange={handlePageChange}
                      hasNextPage={nextPage !== null}
                      hasPreviousPage={previousPage !== null}
                    />
                  </Box>
                )}
                {activeTab === 2 && (
                  <Box>
                    <DataTable
                      columns={billColumns}
                      rows={billRows}
                      CustomNoRowsOverlay={CustomBillNoRowsOverlay}
                      loading={billsLoading}
                      page={billPage}
                      setPage={setBillPage}
                      pageSize={billPageSize}
                      rowCount={billCount}
                      onPageChange={handleBillPageChange}
                      hasNextPage={billNextPage !== null}
                      hasPreviousPage={billPreviousPage !== null}
                    />
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box>
                    <Box sx={{ mb: 1, width: 300 }}>
                      <Autocomplete
                        size="small"
                        options={AllAccounts || []}
                        getOptionLabel={(option) => option.account_name || ""}
                        value={selectedBank}
                        onChange={(_, value) => setSelectedBank(value)}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Bank" />
                        )}
                      />
                    </Box>
                    <DataTable
                      columns={transactionColumns}
                      rows={transactionRows}
                      loading={transactionLoading}
                      page={transactionPage}
                      setPage={setTransactionPage}
                      pageSize={transactionPageSize}
                      rowCount={transactionCount}
                      onPageChange={handleTransactionPageChange}
                      hasNextPage={transactionNextPage !== null}
                      hasPreviousPage={transactionPreviousPage !== null}
                    />
                  </Box>
                )}

                {activeTab === 4 && (
                  <Box>
                    <Box sx={{ mb: 1, width: 300 }}>
                      <TextField
                        size="small"
                        label="Search"
                        value={documentSearchTerm}
                        onChange={handleDocumentSearchChange}
                      />
                    </Box>
                    <DataTable
                      columns={documentColumns}
                      rows={documentRows}
                      loading={documentLoading}
                      page={documentPage}
                      setPage={setDocumentPage}
                      pageSize={docPageSize}
                      rowCount={docCount}
                      onPageChange={handleDocumentPageChange}
                      hasNextPage={docNextPage !== null}
                      hasPreviousPage={docPreviousPage !== null}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomAccordion
                  expanded={contactInfoExpand}
                  onChange={() => setContactInfoExpand(!contactInfoExpand)}
                  title="Contact Information"
                >
                  <Box sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                    <InfoRow
                      label="Phone"
                      value={contactDetail?.mobile}
                      fieldName="phone"
                      icon={Phone}
                      color="primary.main"
                    />
                    <InfoRow
                      label="Mail"
                      value={contactDetail?.reminder_mail}
                      fieldName="email"
                      icon={Email}
                      color="primary.main"
                    />
                    <InfoRow
                      label="Website"
                      value={contactDetail?.website_url}
                      fieldName="website"
                      icon={Language}
                      color="primary.main"
                    />
                    {fullAddress && (
                      <InfoRow
                        label="Address"
                        value={fullAddress}
                        fieldName="address"
                        icon={LocationOn}
                        color="primary.main"
                      />
                    )}
                  </Box>
                </CustomAccordion>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ContactDashboard;
